import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DataSaverContextType {
  dataSaverEnabled: boolean;
  toggleDataSaver: () => void;
  dataUsageMB: number;
}

const DataSaverContext = createContext<DataSaverContextType | undefined>(undefined);

export function DataSaverProvider({ children }: { children: ReactNode }) {
  const [dataSaverEnabled, setDataSaverEnabled] = useState(() => {
    const saved = localStorage.getItem('data-saver-enabled');
    return saved === 'true';
  });

  const [dataUsageMB, setDataUsageMB] = useState(0);

  useEffect(() => {
    // Persist data saver preference
    localStorage.setItem('data-saver-enabled', String(dataSaverEnabled));
  }, [dataSaverEnabled]);

  useEffect(() => {
    // Track data usage if Performance API is available
    if ('performance' in window && 'getEntriesByType' in performance) {
      const resources = performance.getEntriesByType('resource');
      const totalBytes = resources.reduce((sum, resource: any) => {
        return sum + (resource.transferSize || 0);
      }, 0);
      setDataUsageMB(Math.round(totalBytes / (1024 * 1024) * 100) / 100);
    }
  }, []);

  const toggleDataSaver = () => {
    setDataSaverEnabled(prev => !prev);
  };

  return (
    <DataSaverContext.Provider value={{ dataSaverEnabled, toggleDataSaver, dataUsageMB }}>
      {children}
    </DataSaverContext.Provider>
  );
}

export function useDataSaver() {
  const context = useContext(DataSaverContext);
  if (context === undefined) {
    throw new Error('useDataSaver must be used within DataSaverProvider');
  }
  return context;
}
